import { createSlice } from '@reduxjs/toolkit';
import { SPLR_TYPE_CURRENT, SPLR_TYPE_NON_CURRENT } from './PSAppConstants';
import {
  SRO_SBM_PROP_NAME,
  SRO_SBM_USER_ID_PROP_NAME
} from '../../../shared/AppConstants';
import { getSuppliersWithSroSbmPopulated } from '../../../shared/UtilFunctions';

const initialState = {
  prospectiveSupplierRows: [],
  addRowEnabled: true,
  expandedRowIds: []
};

const validateRows = (prospectiveSupplierRows) => {
  if (prospectiveSupplierRows.length > 0)
    return prospectiveSupplierRows.every(
      (supplier) => supplier.name?.length > 0 && supplier?.status
    );
  else return true;
};

const createNonCurrentProspectiveSupplier = (splr, index) => {
  const sdis = splr.prospectiveSupplierSDIS?.map((psSdi) => psSdi.sdi) || [];
  return {
    ...splr,
    sdis,
    name: splr.supplierName,
    id: index,
    type: SPLR_TYPE_NON_CURRENT
  };
};

const createCurrentProspectiveSupplier = (
  splr,
  index,
  suppliersVitalInfoMap
) => {
  const nameMap = suppliersVitalInfoMap['nameMap'];
  const locationMap = suppliersVitalInfoMap['locationMap'];
  const aeRatingMap = suppliersVitalInfoMap['aeRatingMap'];
  const supplierSDIMap = suppliersVitalInfoMap['sdiMap'];
  const sroSBMMap = suppliersVitalInfoMap['sroSbmMap'];

  getSuppliersWithSroSbmPopulated(sroSBMMap, [splr]);

  const address = locationMap
    ? locationMap[splr.supplierNumber]?.split(',')
    : [];
  return {
    ...splr,
    number: splr.supplierNumber,
    name: nameMap ? nameMap[splr.supplierNumber] : '',
    sdis: supplierSDIMap ? supplierSDIMap[splr.supplierNumber] : [],
    city: address?.length > 0 ? address[0] : '',
    region: address?.length > 1 ? address[1]?.trim().split(' ')[0] : '',
    country: address?.length > 1 ? address[1]?.trim().split(' ')[1] : '',
    id: index,
    aeRating: aeRatingMap ? aeRatingMap[splr.supplierNumber] : '',
    sroSbmName: splr[SRO_SBM_PROP_NAME],
    sroSbmUserId: splr[SRO_SBM_USER_ID_PROP_NAME],
    type: SPLR_TYPE_CURRENT
  };
};

const prospectiveSupplierSlice = createSlice({
  name: 'prospectiveSupplierSlice',
  initialState,
  reducers: {
    setProspectiveSuppliers: (state, action) => {
      const nonCurrentProspectiveSuppliers =
        action.payload?.nonCurrentProspectiveSuppliers?.map(
          createNonCurrentProspectiveSupplier
        ) || [];
      const currentProspectiveSuppliers =
        action.payload?.currentProspectiveSuppliers?.map((splr, index) =>
          createCurrentProspectiveSupplier(
            splr,
            index + nonCurrentProspectiveSuppliers.length,
            action.payload.suppliersVitalInfoMap
          )
        ) || [];
      state.prospectiveSupplierRows = [
        ...nonCurrentProspectiveSuppliers,
        ...currentProspectiveSuppliers
      ];
    },
    addSupplier: (state, action) => {
      state.prospectiveSupplierRows.push(action.payload);
      state.addRowEnabled = validateRows(state.prospectiveSupplierRows);
      state.expandedRowIds = [...state.expandedRowIds, action.payload.id];
    },
    editSupplier: (state, action) => {
      const indexToUpdate = state.prospectiveSupplierRows.findIndex(
        (row) => row.id === action.payload.id
      );

      state.prospectiveSupplierRows[indexToUpdate] = action.payload;
      state.addRowEnabled = validateRows(state.prospectiveSupplierRows);
    },
    editKeyOrCompany: (state, action) => {
      const oldRow = state.prospectiveSupplierRows[action.payload.id];

      state.prospectiveSupplierRows[action.payload.id] = {
        ...oldRow,
        ...action.payload.updatedField
      };
      state.addRowEnabled = validateRows(state.prospectiveSupplierRows);
    },
    deleteSupplier: (state, action) => {
      const idToDelete = state.prospectiveSupplierRows.findIndex(
        (row) => row.id === action.payload
      );
      const maxId = Math.max(...state.expandedRowIds);

      state.expandedRowIds = state.expandedRowIds.filter((id) => id !== maxId);

      const newProspectiveSuppliers = state.prospectiveSupplierRows.filter(
        (supplier) => supplier.id !== idToDelete
      );

      state.prospectiveSupplierRows.splice(idToDelete, 1);

      // reset ids
      if (newProspectiveSuppliers.length > 0) {
        state.prospectiveSupplierRows = newProspectiveSuppliers.map(
          (supplier, index) => {
            return {
              ...supplier,
              id: index
            };
          }
        );
      } else state.prospectiveSupplierRows = [];
      state.addRowEnabled = validateRows(state.prospectiveSupplierRows);
    },
    setExpandedRowIds: (state, action) => {
      state.expandedRowIds = action.payload;
    }
  }
});

export const {
  addSupplier,
  editSupplier,
  deleteSupplier,
  setProspectiveSuppliers,
  editKeyOrCompany,
  setExpandedRowIds
} = prospectiveSupplierSlice.actions;

export const prospectiveSupplierReducer = prospectiveSupplierSlice.reducer;
